import { 
  collection, 
  doc, 
  setDoc, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Script para inicializar todas las colecciones de Firebase

const initializeFirebaseCollections = async () => {
  console.log('🔥 Iniciando creación de colecciones en Firebase...');
  
  try {
    // Verificar si ya existen datos
    const categoriesSnapshot = await getDocs(collection(db, 'categories'));
    if (!categoriesSnapshot.empty) {
      console.log('✅ Las colecciones ya están inicializadas en Firebase');
      return;
    }

    console.log('📝 Creando colecciones con datos completos...');
    
    // 1. CATEGORÍAS
    const categoriesData = [
      {
        id: 'cat-rioja',
        name: 'Rioja',
        description: 'Vinos de la región de La Rioja, conocidos por su tradición y calidad excepcional.',
        region: 'La Rioja',
        established: 1925,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'cat-ribera',
        name: 'Ribera del Duero',
        description: 'Vinos tintos de gran cuerpo y estructura de la prestigiosa D.O. Ribera del Duero.',
        region: 'Castilla y León',
        established: 1982,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'cat-rias-baixas',
        name: 'Rías Baixas',
        description: 'Vinos blancos gallegos elaborados principalmente con uva Albariño.',
        region: 'Galicia',
        established: 1988,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'cat-penedes',
        name: 'Penedès',
        description: 'Cuna del cava español y vinos innovadores del Penedès catalán.',
        region: 'Cataluña',
        established: 1960,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'cat-jumilla',
        name: 'Jumilla',
        description: 'Vinos potentes y estructurados de la región de Murcia.',
        region: 'Murcia',
        established: 1966,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    ];

    console.log('📁 Creando categorías...');
    for (const category of categoriesData) {
      await setDoc(doc(db, 'categories', category.id), category);
      console.log(`✅ Categoría: ${category.name}`);
    }

    // 2. BODEGAS
    const wineriesData = [
      {
        id: 'winery-marques-riscal',
        name: 'Marqués de Riscal',
        region: 'Rioja',
        founded: 1858,
        description: 'Una de las bodegas más prestigiosas de La Rioja, pionera en técnicas de vinificación.',
        website: 'https://www.marquesderiscal.com',
        hectares: 1500,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'winery-vega-sicilia',
        name: 'Vega Sicilia',
        region: 'Ribera del Duero',
        founded: 1864,
        description: 'Icónica bodega de Ribera del Duero, creadora del legendario Vega Sicilia Único.',
        website: 'https://www.vega-sicilia.com',
        hectares: 1000,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'winery-codorniu',
        name: 'Codorníu',
        region: 'Penedès',
        founded: 1551,
        description: 'Pioneros en la elaboración de cava, con más de 450 años de tradición familiar.',
        website: 'https://www.codorniu.com',
        hectares: 3000,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'winery-rias-baixas-albarino',
        name: 'Rías Baixas Albariño',
        region: 'Galicia',
        founded: 1988,
        description: 'Especialistas en vinos albariño de las Rías Baixas gallegas.',
        website: 'https://www.riasbaixas.wine',
        hectares: 800,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'winery-bodegas-jumilla',
        name: 'Bodegas Jumilla Premium',
        region: 'Jumilla',
        founded: 1975,
        description: 'Bodega especializada en vinos de Monastrell de la D.O. Jumilla.',
        website: 'https://www.bodegasjumilla.com',
        hectares: 600,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    ];

    console.log('🏭 Creando bodegas...');
    for (const winery of wineriesData) {
      await setDoc(doc(db, 'wineries', winery.id), winery);
      console.log(`✅ Bodega: ${winery.name}`);
    }

    // 3. TIPOS DE VINO
    const typesData = [
      {
        id: 'type-tinto',
        name: 'Tinto',
        color: 'error',
        description: 'Vinos tintos elaborados con uvas de piel oscura, con taninos y estructura.',
        temperature: '16-18°C',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'type-blanco',
        name: 'Blanco',
        color: 'warning',
        description: 'Vinos blancos frescos y aromáticos, elaborados con uvas de piel clara.',
        temperature: '8-10°C',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'type-rosado',
        name: 'Rosado',
        color: 'secondary',
        description: 'Vinos rosados con color intermedio, frescos y versatiles.',
        temperature: '8-10°C',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'type-espumoso',
        name: 'Espumoso',
        color: 'info',
        description: 'Vinos espumosos y cavas con burbujas naturales del proceso de fermentación.',
        temperature: '6-8°C',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    ];

    console.log('🍾 Creando tipos de vino...');
    for (const type of typesData) {
      await setDoc(doc(db, 'types', type.id), type);
      console.log(`✅ Tipo: ${type.name}`);
    }

    // 4. VINOS
    const winesData = [
      {
        id: 'wine-marques-riscal-reserva',
        name: 'Marqués de Riscal Reserva',
        description: 'Un vino tinto elegante con notas de frutas maduras, especias y un toque de vainilla.',
        price: 25.99,
        categoryId: 'cat-rioja',
        wineryId: 'winery-marques-riscal',
        type: 'Tinto',
        year: 2018,
        alcoholContent: 13.5,
        stock: 50,
        imageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'wine-vega-sicilia-unico',
        name: 'Vega Sicilia Único',
        description: 'Uno de los vinos más prestigiosos de España, con gran complejidad y capacidad de guarda.',
        price: 450.99,
        categoryId: 'cat-ribera',
        wineryId: 'winery-vega-sicilia',
        type: 'Tinto',
        year: 2011,
        alcoholContent: 14.5,
        stock: 12,
        imageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'wine-albarino-rias-baixas',
        name: 'Albariño Rías Baixas Premium',
        description: 'Vino blanco fresco y aromático de las costas gallegas, con notas cítricas y minerales.',
        price: 18.50,
        categoryId: 'cat-rias-baixas',
        wineryId: 'winery-rias-baixas-albarino',
        type: 'Blanco',
        year: 2023,
        alcoholContent: 12.5,
        stock: 75,
        imageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'wine-cava-codorniu',
        name: 'Cava Codorníu Gran Reserva',
        description: 'Espumoso tradicional con burbujas finas y persistentes, criado durante 30 meses.',
        price: 24.99,
        categoryId: 'cat-penedes',
        wineryId: 'winery-codorniu',
        type: 'Espumoso',
        year: 2019,
        alcoholContent: 11.5,
        stock: 100,
        imageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'wine-jumilla-monastrell',
        name: 'Jumilla Monastrell Premium',
        description: 'Vino tinto potente de la región de Murcia, elaborado con uva Monastrell.',
        price: 15.75,
        categoryId: 'cat-jumilla',
        wineryId: 'winery-bodegas-jumilla',
        type: 'Tinto',
        year: 2020,
        alcoholContent: 14.5,
        stock: 60,
        imageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      {
        id: 'wine-marques-riscal-verdejo',
        name: 'Marqués de Riscal Verdejo',
        description: 'Vino blanco fresco y expresivo de la Rioja, elaborado con uva Verdejo.',
        price: 12.50,
        categoryId: 'cat-rioja',
        wineryId: 'winery-marques-riscal',
        type: 'Blanco',
        year: 2023,
        alcoholContent: 12.5,
        stock: 40,
        imageUrl: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    ];

    console.log('🍷 Creando vinos...');
    for (const wine of winesData) {
      await setDoc(doc(db, 'wines', wine.id), wine);
      console.log(`✅ Vino: ${wine.name}`);
    }

    console.log('\n🎉 ¡Todas las colecciones han sido creadas exitosamente!');
    console.log('\n📊 Resumen:');
    console.log(`✅ Categorías: ${categoriesData.length}`);
    console.log(`✅ Bodegas: ${wineriesData.length}`);
    console.log(`✅ Tipos: ${typesData.length}`);
    console.log(`✅ Vinos: ${winesData.length}`);

    return { success: true };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
};

// Función para ejecutar desde la consola del navegador
window.initFirebaseCollections = initializeFirebaseCollections;

export default initializeFirebaseCollections; 